import { Injectable } from '@angular/core';

import { AmorphicService } from '../amorphic/amorphic.service';
import { IllustrationRequest } from '../../../classes/wholelife/IllustrationRequest';
import { HtmlPdfController } from '../../../classes/wholelife/basicIllustration/pdf/HtmlPdfController';
import { Agent } from '../../../../common/js/customer/Agent';
import { CoverpathControllerInterface } from '../../interfaces/controller/CoverpathControllerInterface';
import { QuoteForm } from '../../../../coverpath-agent/public/js/components/home-new/quote-or-apply-new/QuoteForm';
import { AmendmentForm } from '../../../../coverpath-agent/public/js/components/amendment/AmendmentFormInterface';
import { CoverpathCustomer } from '../../../classes/coverpath_subclasses/CoverpathCustomer';
import { Policy } from '../../../../common/js/customer/Policy';
import { SafeInviteInfo } from '../../../../common/js/customer/SafeInviteInfo';
import { HtmlPdfContentController } from '../../../classes/wholelife/HtmlPdfContentController';
import { ProductType } from '../../../../coverpath-common/classes/wholelife/HtmlPdfTypes';
import { CsvController } from '../../../classes/wholelife/basicIllustration/csv/CsvController';
import { ProductSpecResponse } from '../../../../common/js/productService/ProductServiceClient';

@Injectable()
export class BasicIllustrationService {
  private htmlPdfController: HtmlPdfController;
  private HtmlPdfContentController: HtmlPdfContentController;
  private csvController: CsvController;

  constructor(private amorphicService: AmorphicService) {
    this.htmlPdfController = (this.amorphicService.controller as CoverpathControllerInterface).htmlPdfController;
    this.HtmlPdfContentController = (
      this.amorphicService.controller as CoverpathControllerInterface
    ).htmlPdfContentController;
    this.csvController = (this.amorphicService.controller as CoverpathControllerInterface).csvController;
  }

  public async generateInForceIllustration(
    illustrationRequest: IllustrationRequest,
    productSpec: ProductSpecResponse,
    inforceProductType: ProductType
  ) {
    return this.HtmlPdfContentController.generateInforceIllustrationPDF(
      illustrationRequest,
      productSpec,
      inforceProductType
    );
  }

  public async generateBasicIllustrationPDF(illustrationRequest: IllustrationRequest, revised: boolean) {
    return Promise.resolve().then(() =>
      this.htmlPdfController.generateBasicIllustrationPDF(illustrationRequest, revised)
    );
  }

  public async generateBasicIllustrationCSV(illustrationRequest: IllustrationRequest, agencyName?) {
    return this.csvController.generateBasicIllustrationCSV(illustrationRequest, agencyName);
  }

  public async generateConceptIllustrationPDF(illustrationRequest: IllustrationRequest, revised: boolean) {
    return Promise.resolve().then(() =>
      this.htmlPdfController.generateConceptIllustrationPDF(illustrationRequest, revised)
    );
  }

  public async generateTransientRevisedBasicIllustrationPDF(
    illustrationRequest: IllustrationRequest,
    amendmentValues: AmendmentForm
  ) {
    return this.htmlPdfController.generateTransientRevisedBasicIllustrationPDF(illustrationRequest, amendmentValues);
  }

  /**
   * @deprecated This function is only here as a bridge for legacy code... creators of Basic Illustrations
   * are probably better off creating an IllustrationRequest from their specific context
   */
  public createDefaultIllustrationRequest(
    customer: CoverpathCustomer,
    agent: Agent,
    isSupplemental: boolean = false,
    safeInviteInfo: SafeInviteInfo = null
  ): IllustrationRequest {
    if (!!safeInviteInfo) {
      return this.htmlPdfController.createDefaultSafeIllustrationRequest(isSupplemental, safeInviteInfo);
    } else {
      return this.htmlPdfController.createDefaultIllustrationRequest(customer, agent, isSupplemental);
    }
  }

  public createIllustrationRequest(
    quote: QuoteForm,
    updatePrimaryCustomer?: boolean,
    primaryAgentFromPolicy?
  ): IllustrationRequest {
    // @ts-ignore
    return this.amorphicService.controller.createIllustrationRequest(
      quote,
      updatePrimaryCustomer,
      primaryAgentFromPolicy
    );
  }

  public createIllustrationRequestFromPolicy(
    policy: Policy,
    policyInfoPayload,
    segmentInfo,
    agent: Agent,
    _newFaceAmount: number,
    _isPartialConversion: boolean,
    _rateClassIFI: string,
    _girAmountToConvert: number,
    mecYear: number
  ): IllustrationRequest {
    // @ts-ignore
    return this.amorphicService.controller.createIllustrationRequestFromPolicy(
      policy,
      policyInfoPayload,
      segmentInfo,
      agent,
      _newFaceAmount,
      _isPartialConversion,
      _rateClassIFI,
      _girAmountToConvert,
      mecYear
    );
  }

  public createConceptIllustrationRequest(illustrationDetails, insuredDetails): IllustrationRequest {
    // @ts-ignore
    return this.amorphicService.controller.createConceptIllustrationRequest(illustrationDetails, insuredDetails);
  }
}
